import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import "./css/Otp.css";
import arrowBack from "../assets/icons/arrow_back_ios.png";

export default function Otp() {
  const location = useLocation();
  const phone = location.state?.phone || "";
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const inputsRef = useRef([]);
  const [timeLeft, setTimeLeft] = useState(90);
  const [formLoading, setFormLoading] = useState(false);
  const [retryOTPDisabled, setRetryOTPDisabled] = useState(false);
  const navigate = useNavigate();

  const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // ✅ Get current language (from localStorage)
  const lang = localStorage.getItem("app_language") || "en";

  // ✅ Translations
  const t = {
    en: {
      title: "Verify OTP",
      subtitle: "We’ve sent an OTP to",
      resend_wait: "Didn’t receive any code? Retry in",
      resend_ready: "Didn’t receive any code?",
      resend_btn: "Resend OTP",
      continue: "Continue",
      success: "Logged in successfully!",
      otp_sent: "The OTP has been sent successfully!",
      otp_failed: "Failed to send OTP. Try again.",
      login_failed: "Failed to log in. Try again.",
    },
    ar: {
      title: "تحقق من الرمز",
      subtitle: "لقد أرسلنا رمز التحقق إلى",
      resend_wait: "لم تستلم الرمز؟ أعد المحاولة خلال",
      resend_ready: "لم تستلم الرمز؟",
      resend_btn: "أعد إرسال الرمز",
      continue: "متابعة",
      success: "تم تسجيل الدخول بنجاح!",
      otp_sent: "تم إرسال رمز التحقق بنجاح!",
      otp_failed: "فشل في إرسال رمز التحقق. حاول مرة أخرى.",
      login_failed: "فشل تسجيل الدخول. حاول مرة أخرى.",
    },
  }[lang];

  // Redirect if phone invalid
  useEffect(() => {
    // Check authentication status
    const authStatus = localStorage.getItem("isAuthenticated") === "true";

    // Redirect if authenticated
    if (authStatus) {
      const preProfileCompleted =
        localStorage.getItem("pre_profile_completed") === "true";
      navigate(preProfileCompleted ? "/dashboard" : "/profile-step-1", {
        replace: true,
      });
    }
    if (!phone || phone.length !== 9) navigate(-1);
  }, [phone, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Handle OTP input change
  const handleChange = (value, index) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) inputsRef.current[index + 1].focus();
      if (value && index === 5) inputsRef.current[index].blur();
    }
  };

  // Handle backspace
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (timeLeft > 0) return;
    setRetryOTPDisabled(true);
    try {
      const response = await fetch(`${AUTH_BASE_URL}/${VERSION}/user/login`, {
        method: "POST",
        body: JSON.stringify({ phone: "971" + phone }),
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();
      setRetryOTPDisabled(false);

      if (data.response_code === 200) {
        showToast(t.otp_sent, "success");
        setOtp(new Array(6).fill(""));
        setTimeLeft(90);
      } else {
        showToast(data.message || t.otp_failed, "error");
      }
    } catch (error) {
      setRetryOTPDisabled(false);
      console.log(error);
      showToast(t.otp_failed, "error");
    }
  };

  const formattedTime = `${Math.floor(timeLeft / 60)}:${String(
    timeLeft % 60
  ).padStart(2, "0")}`;

  // Verify OTP
  const handleVerify = async () => {
    setFormLoading(true);
    setRetryOTPDisabled(true);
    const enteredOtp = otp.join("");
    if (enteredOtp.length === 6) {
      try {
        const response = await fetch(`${AUTH_BASE_URL}/${VERSION}/user/validate/otp`, {
          method: "POST",
          body: JSON.stringify({
            phone: "971" + phone,
            otp: enteredOtp,
            language_preference: lang,
          }),
          headers: { "Content-Type": "application/json" },
        });

        const data = await response.json();
        setFormLoading(false);
        setRetryOTPDisabled(false);

        if (data.response_code === 200) {
          localStorage.setItem("accessToken", data.response.accessToken);
          localStorage.setItem("refreshToken", data.response.refreshToken);
          if (data.response.user?.name) {
            localStorage.setItem("username", data.response.user.name);
          }
          localStorage.setItem("isAuthenticated", "true");
          const preProfileCompleted =
            data.response.user?.pre_profile_completed === true;
          localStorage.setItem(
            "pre_profile_completed",
            preProfileCompleted ? "true" : "false"
          );
          showToast(t.success, "success");
          
          // Determine redirect path based on pre_profile_completed flag
          let redirectPath = preProfileCompleted ? "/dashboard" : "/profile-step-1";
          
          // Override with sessionStorage redirectPath if exists
          const sessionRedirectPath = sessionStorage.getItem("redirectPath");
          if (sessionRedirectPath) {
            redirectPath = sessionRedirectPath;
            sessionStorage.removeItem("redirectPath");
          }
          
          navigate(redirectPath);
        } else {
          showToast(data.message || t.login_failed, "error");
        }
      } catch (error) {
        setFormLoading(false);
        setRetryOTPDisabled(false);
        console.log(error);
        showToast(t.login_failed, "error");
      }
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
      <button className="back-btn" onClick={() => window.history.back()}>
        <img src={arrowBack} alt="Go Back" />
      </button>

      <h2 className="title">{t.title}</h2>
      <p className="subtitle">
        {t.subtitle} +971 {phone}
      </p>

      <div className="otp-inputs">
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            dir="ltr"
            ref={(el) => (inputsRef.current[index] = el)}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="otp-box"
          />
        ))}
      </div>

      <div className="resend">
        {timeLeft > 0 ? (
          <span>
            {t.resend_wait} {formattedTime}
          </span>
        ) : (
          <>
            <span>{t.resend_ready} </span>
            <button
              className="resend-btn"
              disabled={retryOTPDisabled}
              onClick={handleResend}
            >
              {t.resend_btn}
            </button>
          </>
        )}
      </div>

      <button
        className="continue-btn"
        onClick={handleVerify}
        disabled={!isOtpComplete || formLoading}
      >
        {formLoading ? <span className="loader"></span> : t.continue}
      </button>
    </div>
  );
}